import Footer from '@/components/sections/footer';
import { Button } from '@/components/ui/button';
import translateURL from '@/lib/translateUrl';
import type { BlogPostData } from '@/types/blog';
import { Page } from '@growdash/cms-client/generated';
import { Link } from '@remix-run/react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BlogPostProps {
  post: BlogPostData;
  footerPages: Page[];
  locale: string;
}

export default function BlogPost({ post, footerPages, locale }: BlogPostProps) {
  const { t } = useTranslation();
  return (
    <div
      className='min-h-screen bg-background'
      style={{
        background: 'linear-gradient(102.32deg, #E5EFFF 6.48%, #FFFFFF 90.44%)'
      }}
    >
      <main className='container mx-auto px-6 py-24 max-w-4xl'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to={`${translateURL(locale, '/blog')}`}>
            <Button variant='outline' className='group mb-8'>
              <ArrowLeft className='w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1' />
              {t('blog.backBtn')}
            </Button>
          </Link>

          <div className='flex items-center gap-2 text-sm text-muted-foreground mb-4'>
            <Clock className='w-4 h-4' />
            <span>{post.date}</span>
            <span>•</span>
            <span>{post.readTime}</span>
          </div>

          <h1 className='text-4xl font-bold tracking-tight sm:text-5xl mb-8'>
            {post.title}
          </h1>

          {post.image && (
            <div
              className='aspect-video bg-muted rounded-lg mb-8 bg-cover bg-center'
              style={{ backgroundImage: `url(${post.image})` }}
            />
          )}

          <div
            className='prose prose-lg max-w-none prose-headings:font-bold prose-a:text-primary'
            dangerouslySetInnerHTML={{ __html: post.content || '' }}
          />
        </motion.div>
      </main>

      <Footer pages={footerPages} locale={locale} />
    </div>
  );
}
